import { StatCard } from "../design-system/index.js";
import type { NetWorthMetricsResponse } from "../../lib/api-modules/networth.js";

/**
 * Phase 16d (2026-05-10): renders the household's return-metrics tile
 * row beneath the net-worth chart. Source is the new
 * `GET /v1/networth/metrics?window=X` endpoint; the parent widget
 * fetches in parallel with the chart series.
 *
 * v1 surfaces Total / TWR / IRR / Max DD; v1.5 lights up Sharpe + Beta.
 * IRR can be `null` on Newton-Raphson non-convergence — render "—" in
 * that case rather than 0%, which would be misleading.
 *
 * Privacy mode masks tooltip values to `••••` consistently with the
 * chart Y-axis behavior (StatCard's `value` accepts a string so we
 * format here).
 */
interface ReturnMetricsProps {
  readonly metrics: NetWorthMetricsResponse | null | undefined;
  readonly isLoading: boolean;
  readonly hideValues: boolean;
}

export function ReturnMetrics({ metrics, isLoading, hideValues }: ReturnMetricsProps) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <StatCard
            key={i}
            label="—"
            value={hideValues ? "••••" : "Loading…"}
            sub=""
          />
        ))}
      </div>
    );
  }
  if (!metrics || metrics.snapshots_in_window < 2) {
    return null;
  }

  const fmt = (pct: number | null | undefined): string => {
    if (hideValues) return "••••";
    if (pct === null || pct === undefined || !Number.isFinite(pct)) return "—";
    const sign = pct >= 0 ? "+" : "";
    return `${sign}${(pct * 100).toFixed(2)}%`;
  };
  const direction = (
    pct: number | null | undefined,
  ): "up" | "down" | "flat" | null => {
    if (pct === null || pct === undefined || !Number.isFinite(pct)) return null;
    if (pct === 0) return "flat";
    return pct > 0 ? "up" : "down";
  };
  const periodLabel = `${metrics.snapshots_in_window} snapshots / ${metrics.period_days}d`;

  const totalDir = direction(metrics.total_pct);
  const twrDir = direction(metrics.twr_pct);
  const irrDir = direction(metrics.irr_pct);
  const ddDir = metrics.max_drawdown_pct < 0 ? "down" : null;

  return (
    <div
      className="grid grid-cols-2 gap-3 sm:grid-cols-4"
      data-testid="return-metrics"
    >
      <StatCard
        label="Total return"
        value={fmt(metrics.total_pct)}
        sub={periodLabel}
        {...(totalDir !== null && { trendDirection: totalDir })}
      />
      <StatCard
        label="TWR"
        value={fmt(metrics.twr_pct)}
        sub="Time-weighted (asset-mgr standard)"
        {...(twrDir !== null && { trendDirection: twrDir })}
      />
      <StatCard
        label="IRR"
        value={fmt(metrics.irr_pct)}
        sub="Money-weighted (your experience)"
        {...(irrDir !== null && { trendDirection: irrDir })}
      />
      <StatCard
        label="Max drawdown"
        value={fmt(metrics.max_drawdown_pct)}
        sub="Largest peak-to-trough"
        {...(ddDir !== null && { trendDirection: ddDir })}
      />
    </div>
  );
}
